// professionalsVmBuilder.js — monta o ProfessionalsVM (@version 2) a partir da massa crua da API.
//
// Função PURA: recebe dados, devolve o vm. Nenhum fetch, nenhum hook, nenhum estado. É aqui que
// mora a regra de negócio da tela — a View só lê o resultado.
//
// ────────────────────────────────────────────────────────────────────────────
// "ESTA PESSOA PODE SER AGENDADA?" — de onde vem a resposta
// ────────────────────────────────────────────────────────────────────────────
// Nada é refeito aqui. O veredito de cada AGENDA vem de `buildScheduleCard` (Agendas) e o de cada
// SERVIÇO vem de `motivoDoServico` (Locais): a mesma regra das duas telas, olhando só as agendas
// DESTA pessoa naquele local. Profissionais é o outro lado de Locais: lá, quem atende em cada lugar;
// aqui, onde cada pessoa atende.
//
// Pausar é decisão do dono, nunca pendência (isByChoice). Recebido de outra conta: o dono vê, mas
// não conserta — as pendências aparecem sem o botão de conserto.
//
// ────────────────────────────────────────────────────────────────────────────
// TEXTOS SEM GÊNERO
// ────────────────────────────────────────────────────────────────────────────
// O container não sabe se é a Ana ou o Rafael, nem se o local é "Unidade" ou "Studio" (13 temas,
// 29/10/2026), e o tema não tem o singular do nicho ("Barbeiros" é o título). Por isso: nada de
// ele/ela, "colocada", "na/no" antes do nome do local; o nome entra como foi cadastrado.

import { formatDuration, formatTime } from '../../utils/formatters';
import { getInitials, removeTitle } from '../../utils/professionalNameUtils';
import { buildScheduleCard } from '../schedules/buildSchedulesVm';
import { mapaDePermissoes, ENTIDADES } from '../access/accessVmBuilder';
import {
  motivoDoServico, montarTurnos, diaBRT, quando, diaCurto, mesAno, diasEntre, plural, texto, lista,
  precoLabel, montarEndereco, montarContato, montarAgendaDoLocal, TIPO_DE_PAGINA, TIPO_DE_PAGAMENTO, PAGES_URL,
} from '../places/placesVmBuilder';

const BRT = 'America/Sao_Paulo';
const DIA_MS = 86_400_000;
const AUSENCIA_A_VISTA_DIAS = 14;

const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Nome A → Z' },
  { value: 'name_desc', label: 'Nome Z → A' },
  { value: 'specialty_asc', label: 'Especialidade' },
  { value: 'next_appointment', label: 'Próximo atendimento' },
  { value: 'busiest', label: 'Mais agendados' },
];

const ESCOPO_DA_PAGINA = { account: 'De toda a conta', professional: 'Exclusiva' };

// [v2] As áreas do compartilhamento com os nomes de Gestão de Acessos — a mesma fonte, o mesmo vocabulário.
const AREAS_DO_COMPARTILHAMENTO = ENTIDADES.map(({ key, label }) => ({ key, label }));

const MOTIVO = {
  notOffered: 'Nenhum turno da agenda oferece este serviço.',
  broken: 'A agenda daqui não oferece horário.',
  paused: 'A agenda está pausada por você.',
  noSchedule: 'Não há agenda aqui.',
  hidden: 'Só a outra conta vê as agendas.',
};

/** Recebido sem acesso às agendas: o dono não vê os turnos — não dá para dizer se há horário. */
const agendasOcultas = (prof) => !!prof.isShared && niveisDoRecebido(prof.permissions).schedules === 0;

// As 4 áreas do compartilhamento, com artigo, para as frases de acesso.
const AREAS = [
  { key: 'professional', nome: 'o cadastro' },
  { key: 'appointments', nome: 'os agendamentos' },
  { key: 'schedules', nome: 'as agendas' },
  { key: 'persons', nome: 'os clientes' },
];

// O que a edição deixa completar (ProfessionalForm) — cada chave abre a edição já no campo.
const CAMPOS_DO_PERFIL = [
  { key: 'specialty', label: 'Especialidade', tem: (p) => !!texto(p.specialty) },
  { key: 'title', label: 'Título', tem: (p) => !!texto(p.title) },
  { key: 'registration', label: 'Registro profissional', tem: (p) => !!texto(p.registrationNumber) },
  { key: 'phone', label: 'Telefone', tem: (p) => !!texto(p.phone) },
  { key: 'email', label: 'E-mail', tem: (p) => !!texto(p.email) },
  { key: 'bio', label: 'Bio', tem: (p) => !!texto(p.bio) },
  { key: 'description', label: 'Descrição', tem: (p) => !!texto(p.description) },
];

const normalizar = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const nomeSemTitulo = (p) => removeTitle(p.name || '') || p.name || '';
const porNomeSemTitulo = (a, b) => nomeSemTitulo(a.prof).localeCompare(nomeSemTitulo(b.prof), 'pt-BR');
const fix = (type, o = {}) => ({ type, placeId: null, scheduleId: null, ...o });

/** '12/06/2026' (dia de Brasília). */
function dataLonga(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('pt-BR', { timeZone: BRT, day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
}

/** 'Hoje, 14:30' | 'Amanhã, 09:00' | 'Qui, 30/10 · 15:00' (nesta semana) | '15/11 · 10:00' */
function rotuloDoHorario(iso, now) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dia = diaBRT(d);
  const hora = formatTime(iso);
  if (dia === diaBRT(now)) return `Hoje, ${hora}`;
  if (dia === diaBRT(new Date(now.getTime() + DIA_MS))) return `Amanhã, ${hora}`;
  const [, m, dd] = dia.split('-');
  const distancia = (Date.parse(dia) - Date.parse(diaBRT(now))) / DIA_MS;
  if (distancia > 0 && distancia < 7) {
    const semana = new Intl.DateTimeFormat('pt-BR', { timeZone: BRT, weekday: 'short' }).format(d).replace('.', '');
    return `${semana.charAt(0).toUpperCase()}${semana.slice(1)}, ${dd}/${m} · ${hora}`;
  }
  return `${dd}/${m} · ${hora}`;
}

// ── rótulos do nicho ──────────────────────────────────────────────────────────

/** Tema » labels → ProfessionalsLabels. `examples` vem do tema como "Ex: A, B, C". */
export function buildProfessionalsLabels(getLabel) {
  const title = getLabel?.('professionals') || 'Profissionais';
  const exemplos = String(getLabel?.('professionalsExamples') || '')
    .replace(/^\s*ex(emplos?)?\s*:\s*/i, '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return {
    labels: {
      title,
      subtitle: getLabel?.('professionalsDescription') || 'Quem atende no seu negócio',
      actionLabel: 'Adicionar à equipe',
      searchPlaceholder: 'Buscar por nome, especialidade, telefone ou registro',
      ownGroupTitle: 'Sua equipe',
      receivedGroupTitle: 'De outras contas',
      receivedGroupDescription: `${title} que outras contas abriram para você`,
    },
    examples: exemplos,
  };
}

// ── acesso (próprio × recebido) ───────────────────────────────────────────────

/** `permissions` do GET /professionals ({ "1": 1, "2": 2 } — chave = AccessEntityType) → PermMap. */
function niveisDoRecebido(permissions) {
  const n = (k) => Number(permissions?.[k] ?? permissions?.[String(k)] ?? 0) || 0;
  return { professional: n(1), appointments: n(2), schedules: n(3), persons: n(4) };
}

/** "Você pode agendar e ver as agendas" — o que o dono pode fazer com um recebido. */
function oQueVocePode(niveis) {
  const partes = [];
  if (niveis.professional === 2) partes.push('editar o cadastro');
  if (niveis.appointments === 2) partes.push('agendar');
  else if (niveis.appointments === 1) partes.push('ver os agendamentos');
  if (niveis.schedules === 2) partes.push('editar as agendas');
  else if (niveis.schedules === 1) partes.push('ver as agendas');
  if (niveis.persons === 2) partes.push('editar os clientes');
  else if (niveis.persons === 1) partes.push('ver os clientes');
  return partes.length ? `Você pode ${lista(partes)}` : 'Você só vê o cadastro';
}

/** "Edita o cadastro e os agendamentos e vê as agendas" — o que quem recebeu pode fazer. */
function oQueAOutraContaFaz(niveis) {
  const edita = AREAS.filter((a) => niveis[a.key] === 2).map((a) => a.nome);
  const ve = AREAS.filter((a) => niveis[a.key] === 1).map((a) => a.nome);
  if (edita.length && ve.length) return `Edita ${lista(edita)} e vê ${lista(ve)}`;
  if (edita.length) return `Edita ${lista(edita)}`;
  return `Vê ${lista(ve)}`;
}

function montarDono(prof) {
  if (!prof.isShared) {
    return {
      kind: 'own', ownerName: null, accessLabel: null, permissionLevels: null,
      canEdit: true, canDelete: true, canShare: true, canChangePhoto: true, canEditPrices: true, canBook: true,
      readOnlyReason: null,
    };
  }
  const niveis = niveisDoRecebido(prof.permissions);
  const dono = texto(prof.ownerName) || 'a outra conta';
  return {
    kind: 'received',
    ownerName: texto(prof.ownerName),
    accessLabel: oQueVocePode(niveis),
    permissionLevels: niveis,
    canEdit: niveis.professional === 2,
    canDelete: false, canShare: false, canChangePhoto: false, canEditPrices: false,
    canBook: niveis.appointments === 2,
    readOnlyReason: niveis.professional === 2 ? null : `Só ${dono} altera o cadastro.`,
  };
}

// ── onde a pessoa atende: o cruzamento por local ──────────────────────────────

/**
 * Um local onde a pessoa atende: os serviços dela lá, as agendas dela lá e por que cada serviço
 * aparece ou não para o cliente. `multi` = a pessoa atende em mais de um local (os rótulos dizem onde).
 */
function analisarLugar(prof, link, ctx, multi) {
  const place = ctx.locais.get(link.placeId) || { id: link.placeId, name: 'Local' };
  const agendas = ctx.agendasDe(prof.id, link.placeId);
  const ocultas = agendasOcultas(prof);
  const servicos = (link.services || []).filter((s) => s.serviceId).map((s) => ({
    link: s,
    servico: ctx.servicos.get(s.serviceId) || {},
    motivo: ocultas ? 'hidden' : motivoDoServico(agendas, s.serviceId, ctx.turnos),
  }));
  const readiness = ocultas ? prontidaoDesconhecida(prof) : prontidaoDoLugar(prof, place, agendas, servicos, ctx, multi);
  return { place, link, agendas, servicos, readiness };
}

function prontidaoDoLugar(prof, place, agendas, servicos, ctx, multi) {
  const nome = prof.name;
  const onde = multi ? `Em ${place.name}, ` : '';
  const conserta = !prof.isShared;
  const comConserto = (issue, actionLabel, f) => ({ ...issue, actionLabel: conserta ? actionLabel : null, fix: conserta ? f : null });
  const issues = [];

  const operando = agendas.filter((g) => g.card.operation.isOperating);
  const quebradas = agendas.filter((g) => !g.card.operation.isOperating && !g.card.operation.isByChoice);
  const pausadas = agendas.filter((g) => g.card.operation.isByChoice);
  const recebem = servicos.filter((s) => s.motivo === null);
  const isBookable = recebem.length > 0;

  if (servicos.length === 0) {
    issues.push(comConserto({
      key: 'noService', label: `Sem serviço em ${place.name}`,
      why: `${nome} atende em ${place.name}, mas sem nenhum serviço lá: o cliente não tem o que escolher.`,
      status: 'error', isByChoice: false,
    }, 'Escolher os serviços de lá', fix('configureServices', { placeId: place.id })));
  } else if (agendas.length === 0) {
    issues.push(comConserto({
      key: 'noSchedule', label: multi ? `Sem agenda em ${place.name}` : 'Sem agenda',
      why: `${nome} tem lugar e serviços, mas não há agenda${multi ? ` em ${place.name}` : ''}. É a agenda que diz em que dias e horários a pessoa atende; sem agenda, não sai horário nenhum.`,
      status: 'error', isByChoice: false,
    }, 'Criar a agenda', fix('createSchedule', { placeId: place.id })));
  }

  for (const g of quebradas) {
    issues.push(comConserto({
      key: 'scheduleNotOperating', label: `${onde}${onde ? 'a' : 'A'} agenda "${g.raw.name}" não oferece horário`,
      why: g.card.operation.detail || 'A agenda está ligada, mas não gera horário para o cliente.',
      status: operando.length > 0 ? 'warning' : 'error', isByChoice: false,
    }, 'Abrir a agenda', fix('openSchedule', { placeId: place.id, scheduleId: g.raw.id })));
  }

  const semTurno = servicos.filter((s) => s.motivo === 'notOffered');
  if (semTurno.length > 0) {
    const nomes = semTurno.map((s) => s.servico.name || 'Serviço');
    const agenda = operando[0];
    issues.push(comConserto({
      key: 'servicesWithoutShift',
      label: `${onde}${semTurno.length === 1 ? `1 serviço sem horário: ${nomes[0]}` : `${semTurno.length} serviços sem horário: ${lista(nomes)}`}`,
      why: semTurno.length === 1
        ? `O serviço está no local, mas nenhum turno da agenda "${agenda.raw.name}" o oferece, então o cliente não vê horário.`
        : `${lista(nomes)} estão no local, mas nenhum turno da agenda "${agenda.raw.name}" os oferece, então o cliente não vê horário.`,
      status: isBookable ? 'warning' : 'error', isByChoice: false,
    }, 'Abrir a agenda', fix('openSchedule', { placeId: place.id, scheduleId: agenda.raw.id })));
  }

  const tudoPausado = !isBookable && servicos.length > 0 && servicos.every((s) => s.motivo === 'paused');
  if (pausadas.length > 0 && (tudoPausado || operando.length > 0 || quebradas.length > 0)) {
    const g = pausadas[0];
    issues.push(comConserto({
      key: 'schedulesPaused',
      label: pausadas.length === 1 ? `${onde}${onde ? 'a' : 'A'}genda "${g.raw.name}" pausada` : `${onde}${pausadas.length} agendas pausadas`,
      why: tudoPausado
        ? `Foi você quem pausou. Enquanto estiver pausada, ${nome} não aparece para o cliente marcar${multi ? ` em ${place.name}` : ''}, e nada se perde: é só reativar.`
        : 'Foi você quem pausou. Nada se perde: é só reativar.',
      status: 'neutral', isByChoice: true,
    }, 'Ver a agenda', fix('openSchedule', { placeId: place.id, scheduleId: g.raw.id })));
  }

  if (tudoPausado) {
    return {
      isBookable: false, isByChoice: true, status: 'neutral',
      headline: pausadas.length === 1 ? 'Agenda pausada por você' : 'Agendas pausadas por você',
      detail: 'Não recebe agendamento aqui até você reativar a agenda.',
      issues,
    };
  }

  if (!isBookable) {
    const tem = (k) => issues.some((i) => i.key === k);
    let detail;
    if (tem('noService')) detail = 'Sem nenhum serviço aqui.';
    else if (tem('noSchedule')) detail = 'Falta a agenda aqui.';
    else if (quebradas.length > 0 && operando.length === 0) {
      detail = quebradas.length === 1 ? `A agenda "${quebradas[0].raw.name}" não oferece horário.` : 'As agendas daqui não oferecem horário.';
    } else detail = 'Nenhum turno oferece os serviços daqui.';
    return {
      isBookable: false, isByChoice: false, status: 'error', headline: 'Não recebe agendamento aqui', detail,
      issues: issues.map((i) => (i.status === 'warning' ? { ...i, status: 'error' } : i)),
    };
  }

  const defeitos = issues.filter((i) => i.status === 'warning' || i.status === 'error');
  const fora = servicos.filter((s) => s.motivo !== null && s.motivo !== 'paused');
  let detail = null;
  if (fora.length === 1) detail = `Mas ${fora[0].servico.name || 'um serviço'} não aparece para o cliente marcar.`;
  else if (fora.length > 1) detail = `Mas ${fora.length} dos ${servicos.length} serviços não aparecem para o cliente marcar.`;
  else if (defeitos.length > 0) detail = 'Mas uma das agendas daqui não oferece horário.';
  return { isBookable: true, isByChoice: false, status: defeitos.length > 0 ? 'warning' : 'success', headline: 'Recebe agendamento', detail, issues };
}

/** Recebido sem acesso às agendas: só a outra conta sabe se há horário — calmo, nunca acusação. */
function prontidaoDesconhecida(prof) {
  return {
    isBookable: false, isByChoice: true, status: 'neutral',
    headline: 'Agendas de outra conta',
    detail: `Só ${texto(prof.ownerName) || 'a outra conta'} vê se há horário: você não tem acesso às agendas.`,
    issues: [],
  };
}

/** O veredito da pessoa: junta os locais. Recebe em algum = recebe; o resto vira "mas…". */
function prontidaoDaPessoa(prof, lugares) {
  if (agendasOcultas(prof)) return prontidaoDesconhecida(prof);

  if (lugares.length === 0) {
    const conserta = !prof.isShared;
    return {
      isBookable: false, isByChoice: false, status: 'error',
      headline: 'Não aparece para o cliente agendar',
      detail: 'Ainda sem lugar de atendimento.',
      issues: [{
        key: 'noPlace', label: 'Ainda sem lugar de atendimento',
        why: `Os serviços e a agenda ficam no lugar onde a pessoa atende. Sem um lugar, ${prof.name} não aparece na página para o cliente marcar.`,
        status: 'error', isByChoice: false,
        actionLabel: conserta ? 'Escolher onde atende' : null,
        fix: conserta ? fix('linkPlace') : null,
      }],
    };
  }

  const recebe = lugares.filter((l) => l.readiness.isBookable);
  const travados = lugares.filter((l) => !l.readiness.isBookable && !l.readiness.isByChoice);

  if (recebe.length > 0) {
    // Onde não recebe, pesa como "parte não aparece": a pessoa recebe em outro lugar.
    const issues = [
      ...travados.flatMap((l) => l.readiness.issues.map((i) => (i.status === 'error' ? { ...i, status: 'warning' } : i))),
      ...lugares.filter((l) => !travados.includes(l)).flatMap((l) => l.readiness.issues),
    ].sort((a, b) => Number(a.status === 'neutral' || a.status === 'info') - Number(b.status === 'neutral' || b.status === 'info'));
    const defeitos = issues.filter((i) => i.status === 'warning' || i.status === 'error');
    const servicosFora = recebe.flatMap((l) => l.servicos.filter((s) => s.motivo !== null && s.motivo !== 'paused'));
    const nomesFora = servicosFora.map((s) => s.servico.name || 'Serviço');
    const ondeNada = lista(travados.map((l) => l.place.name));
    let detail = null;
    if (servicosFora.length > 0) {
      const oQue = servicosFora.length === 1 ? `${nomesFora[0]} não aparece` : `${servicosFora.length} serviços não aparecem`;
      detail = travados.length > 0
        ? `Mas ${oQue} para o cliente marcar, e em ${ondeNada} nada aparece.`
        : `Mas ${oQue} para o cliente marcar.`;
    } else if (travados.length > 0) {
      detail = `Mas em ${ondeNada} nada aparece para o cliente marcar.`;
    } else if (defeitos.length > 0) {
      detail = 'Mas uma das agendas não oferece horário.';
    }
    return {
      isBookable: true, isByChoice: false, status: defeitos.length > 0 ? 'warning' : 'success',
      headline: 'Recebe agendamento', detail, issues,
    };
  }

  if (travados.length === 0) {
    const pausadas = lugares.reduce((n, l) => n + l.agendas.filter((g) => g.card.operation.isByChoice).length, 0);
    return {
      isBookable: false, isByChoice: true, status: 'neutral',
      headline: pausadas > 1 ? 'Agendas pausadas por você' : 'Agenda pausada por você',
      detail: 'Não recebe agendamento até você reativar a agenda.',
      issues: lugares.flatMap((l) => l.readiness.issues),
    };
  }

  const primeiro = travados[0];
  const tem = (k) => primeiro.readiness.issues.some((i) => i.key === k);
  let detail;
  if (tem('noService')) detail = `Atende em ${primeiro.place.name}, mas sem nenhum serviço lá.`;
  else if (tem('noSchedule')) detail = 'Tem lugar e serviços, mas falta a agenda.';
  else if (tem('scheduleNotOperating') && !tem('servicesWithoutShift')) detail = 'A agenda está ligada, mas não oferece horário.';
  else detail = 'Nenhum turno oferece os serviços desta pessoa.';
  return {
    isBookable: false, isByChoice: false, status: 'error',
    headline: 'Não aparece para o cliente agendar', detail,
    issues: lugares.flatMap((l) => l.readiness.issues).map((i) => (i.status === 'warning' ? { ...i, status: 'error' } : i)),
  };
}

// ── preços: o que esta pessoa cobra em cada forma ─────────────────────────────

function montarPrecos(link, servico, ctx) {
  const proprios = new Map((link.customPrices || [])
    .filter((c) => c.customPrice != null)
    .map((c) => [c.placeServicePaymentId, c.customPrice]));
  return [...(link.placePrices || [])]
    .filter((pp) => ctx.pagamentos.has(pp.paymentId))
    .sort((p, q) => (ctx.pagamentos.get(p.paymentId).displayOrder ?? 999) - (ctx.pagamentos.get(q.paymentId).displayOrder ?? 999))
    .map((pp) => {
      const forma = ctx.pagamentos.get(pp.paymentId);
      const proprio = proprios.has(pp.placeServicePaymentId);
      const efetivo = proprio ? proprios.get(pp.placeServicePaymentId) : pp.defaultPrice;
      return {
        paymentName: forma.name,
        paymentType: TIPO_DE_PAGAMENTO[forma.type] || 'other',
        priceLabel: precoLabel(efetivo),
        isDefault: (efetivo ?? null) === (servico.defaultPrice ?? null),
        isToBeAgreed: efetivo == null,
        isCustom: proprio,
        placePriceLabel: proprio ? `No local: ${precoLabel(pp.defaultPrice)}` : null,
      };
    });
}

function horariosLivres(estado, now) {
  if (!estado) return { state: 'idle', slots: [], message: null };
  if (estado.state === 'loading') return { state: 'loading', slots: [], message: null };
  if (estado.state === 'error') return { state: 'error', slots: [], message: 'Não deu para buscar agora. Tente de novo.' };
  const slots = (estado.slots || []).filter((s) => s?.startTime).slice(0, 3);
  if (slots.length === 0) return { state: 'empty', slots: [], message: 'Nenhum horário livre nos próximos 30 dias' };
  return { state: 'loaded', slots: slots.map((s) => ({ key: s.startTime, label: rotuloDoHorario(s.startTime, now) })), message: null };
}

/** Chave do estado de "próximos horários" — o container guarda as respostas por ela. */
export const chaveDosHorarios = (professionalId, placeId, serviceId) => `${professionalId}|${placeId}|${serviceId}`;

function montarServicoDoLugar(prof, l, s, ctx, now) {
  const servico = s.servico;
  const prices = montarPrecos(s.link, servico, ctx);
  const proprios = prices.filter((p) => p.isCustom).map((p) => p.paymentName);
  return {
    serviceId: s.link.serviceId,
    name: servico.name || 'Serviço',
    category: texto(servico.category),
    durationLabel: servico.defaultDurationMinutes ? formatDuration(servico.defaultDurationMinutes) : null,
    priceLabel: precoLabel(servico.defaultPrice),
    basePriceIsToBeAgreed: servico.defaultPrice == null,
    prices,
    hasCustomPrices: proprios.length > 0,
    customPricesLabel: proprios.length ? `Preço próprio em ${lista(proprios)}` : null,
    isBookable: s.motivo === null,
    notBookableReason: s.motivo ? MOTIVO[s.motivo] : null,
    // Pausa é escolha (neutro), e a agenda que só a outra conta vê também não é defeito do dono.
    // Defeito pesa como pesa no local: se por isso nada recebe aqui, impede.
    notBookableStatus: !s.motivo ? null
      : s.motivo === 'paused' || s.motivo === 'hidden' ? 'neutral'
        : l.readiness.isBookable ? 'warning' : 'error',
    notBookableIsByChoice: s.motivo === 'paused' || s.motivo === 'hidden',
    nextSlots: horariosLivres(ctx.horarios[chaveDosHorarios(prof.id, l.place.id, s.link.serviceId)], now),
  };
}

// ── ausências ────────────────────────────────────────────────────────────────

/** Ausências em vigor e futuras da pessoa, juntando os blocos iguais criados um por local. */
function ausenciasDaPessoa(prof, ctx, now) {
  const vinculos = new Set((prof.places || []).map((l) => l.id));
  const grupos = new Map();
  for (const b of ctx.bloqueios) {
    if (!vinculos.has(b.placeProfessionalId)) continue;
    if (!b.endDate || Date.parse(b.endDate) < now.getTime()) continue;
    const chave = `${b.startDate}|${b.endDate}|${texto(b.reason) || ''}`;
    if (!grupos.has(chave)) grupos.set(chave, { id: b.id, startDate: b.startDate, endDate: b.endDate, reason: texto(b.reason), placeIds: new Set() });
    const placeId = ctx.localDoVinculo.get(b.placeProfessionalId);
    if (placeId) grupos.get(chave).placeIds.add(placeId);
  }
  const todos = (prof.places || []).length;
  return [...grupos.values()]
    .sort((a, b) => Date.parse(a.startDate) - Date.parse(b.startDate))
    .map((g) => ({
      ...g,
      isNow: Date.parse(g.startDate) <= now.getTime(),
      todosOsLocais: g.placeIds.size === 0 || g.placeIds.size >= todos,
      nomes: [...g.placeIds].map((id) => ctx.locais.get(id)?.name).filter(Boolean),
    }));
}

function notaDeAusencia(a, now) {
  if (!a) return null;
  return {
    isNow: a.isNow,
    label: a.isNow ? `Ausente até ${diaCurto(a.endDate, now)}` : `Ausente de ${diaCurto(a.startDate, now)} a ${diaCurto(a.endDate, now)}`,
    reason: a.reason,
    placesLabel: a.todosOsLocais ? 'Em todos os locais' : `Em ${lista(a.nomes)}`,
    status: a.isNow ? 'warning' : 'info',
  };
}

// ── o cartão ─────────────────────────────────────────────────────────────────

function movimentoDaPessoa(prof, ctx) {
  const agendas = ctx.agendasDaPessoa(prof.id);
  const futuros = agendas.reduce((s, g) => s + (g.raw.futureAppointmentsCount || 0), 0);
  const proximo = agendas.map((g) => g.raw.nextAppointmentAt).filter(Boolean).sort((x, y) => Date.parse(x) - Date.parse(y))[0] || null;
  const a = ctx.atividade.get(prof.id);
  return {
    futuros,
    proximo,
    ultimos30: a?.last30Days || 0,
    online30: a?.onlineLast30Days || 0,
    ultimo: a?.lastAppointmentAt || null,
    aguardando: a?.awaitingConfirmation || 0,
  };
}

function montarAtividade(m, now) {
  return {
    futureCount: m.futuros,
    futureLabel: m.futuros > 0 ? plural(m.futuros, 'marcado', 'marcados') : null,
    nextAppointmentLabel: m.proximo ? `Próximo: ${quando(m.proximo, now)}` : null,
    last30DaysLabel: m.ultimos30 > 0 ? `${plural(m.ultimos30, 'atendimento', 'atendimentos')} nos últimos 30 dias` : null,
    onlineShareLabel: m.ultimos30 > 0 && m.online30 > 0
      ? `${Math.round((m.online30 / m.ultimos30) * 100)}% ${m.online30 === 1 && m.ultimos30 === 1 ? 'chegou' : 'chegaram'} pela página ou pelo link`
      : null,
    lastAppointmentLabel: m.futuros === 0 && m.ultimo ? `Último atendimento: ${diaCurto(m.ultimo, now)}` : null,
    emptyLabel: m.futuros === 0 && !m.ultimo ? 'Ainda sem nenhum agendamento' : null,
    awaitingConfirmationCount: m.aguardando,
    awaitingConfirmationLabel: m.aguardando > 0 ? `${m.aguardando} aguardando confirmação` : null,
  };
}

function montarCard(prof, ctx, now) {
  const links = (prof.places || []).filter((l) => l.placeId);
  const multi = links.length > 1;
  const lugares = links.map((link) => analisarLugar(prof, link, ctx, multi))
    .sort((a, b) => String(a.place.name).localeCompare(String(b.place.name), 'pt-BR'));
  const readiness = prontidaoDaPessoa(prof, lugares);
  const ownership = montarDono(prof);
  const mov = movimentoDaPessoa(prof, ctx);
  const recebido = !!prof.isShared;
  const paginas = recebido ? [] : (ctx.paginas.get(prof.id) || []);
  const compartilhado = recebido ? [] : (ctx.compartilhadoCom.get(prof.id) || []);
  const ausencias = ausenciasDaPessoa(prof, ctx, now);
  const aVista = ausencias.find((a) => a.isNow || Date.parse(a.startDate) - now.getTime() < AUSENCIA_A_VISTA_DIAS * DIA_MS) || null;
  const nomesDosLocais = lugares.map((l) => l.place.name);
  const servicosDistintos = new Set(lugares.flatMap((l) => l.servicos.map((s) => s.link.serviceId))).size;
  const contato = montarContato(prof);
  const titulo = texto(prof.title);
  const desde = recebido ? ctx.recebidoDesde.get(prof.id) : prof.createdAt;

  const card = {
    id: prof.id,
    name: prof.name,
    titleLabel: titulo && !normalizar(prof.name).startsWith(normalizar(titulo)) ? titulo : null,
    initials: getInitials(prof.name),
    photoUrl: ctx.fotoDe(prof.ownerId, prof.id),
    specialty: texto(prof.specialty),
    registration: texto(prof.registrationNumber),
    contact: {
      phoneLabel: contato.phoneLabel, phoneHref: contato.phoneHref, whatsappHref: contato.whatsappHref,
      email: contato.email, emailHref: contato.emailHref,
    },
    ownership,
    sharing: recebido ? null : {
      count: compartilhado.length,
      label: compartilhado.length ? `${plural(compartilhado.length, 'pessoa', 'pessoas')} com acesso` : null,
      names: compartilhado.map((c) => c.grantedToName).filter(Boolean),
    },
    placesSummary: {
      count: lugares.length,
      label: lugares.length === 0 ? 'Nenhum local' : lugares.length === 1 ? nomesDosLocais[0] : `${lugares.length} locais`,
      names: nomesDosLocais,
    },
    servicesSummary: {
      count: servicosDistintos,
      label: servicosDistintos === 0 ? 'Nenhum serviço' : plural(servicosDistintos, 'serviço', 'serviços'),
    },
    readiness,
    activity: montarAtividade(mov, now),
    presence: {
      pagesCount: paginas.length,
      label: paginas.length ? `Em ${plural(paginas.length, 'página', 'páginas')} no ar` : null,
      hasOwnPage: paginas.some((p) => p.scope === 'professional'),
    },
    absence: notaDeAusencia(aVista, now),
    sinceLabel: desde ? `${recebido ? 'Com você' : 'Na equipe'} desde ${mesAno(desde)}` : '',
  };
  return { prof, card, lugares, mov, paginas, compartilhado, ausencias, peso: mov.futuros + mov.ultimos30 };
}

// ── a pessoa aberta ──────────────────────────────────────────────────────────

function montarDetalhe(x, ctx, now) {
  const { prof, card, mov } = x;
  const podeEditar = card.ownership.canEdit;
  const faltam = podeEditar ? CAMPOS_DO_PERFIL.filter((c) => !c.tem(prof)) : [];
  const total = CAMPOS_DO_PERFIL.length + 1; // + o nome, que sempre existe
  const editado = prof.updatedAt && prof.createdAt && Date.parse(prof.updatedAt) - Date.parse(prof.createdAt) > 60_000;

  const stats = [];
  if (mov.ultimos30 > 0 || mov.futuros > 0 || mov.ultimo) {
    stats.push({ key: 'last30Days', label: 'Atendimentos nos últimos 30 dias', value: String(mov.ultimos30), hint: mov.ultimos30 === 0 && mov.ultimo ? `O último foi em ${diaCurto(mov.ultimo, now)}.` : null });
    if (mov.ultimos30 > 0) {
      stats.push({ key: 'online', label: 'Chegaram pela página ou pelo link', value: `${Math.round((mov.online30 / mov.ultimos30) * 100)}%`, hint: 'O resto foi lançado por você ou pela recepção no painel.' });
    }
    stats.push({ key: 'future', label: 'Agendamentos marcados', value: String(mov.futuros), hint: mov.proximo ? `Próximo: ${quando(mov.proximo, now)}` : null });
    if (mov.aguardando > 0) {
      stats.push({ key: 'awaitingConfirmation', label: 'Aguardando confirmação', value: String(mov.aguardando), hint: 'Confirme em Agendamentos.' });
    }
  }

  return {
    id: prof.id,
    card,
    bio: texto(prof.bio),
    description: texto(prof.description),
    updatedLabel: editado ? `Editado em ${dataLonga(prof.updatedAt)}` : null,
    profileCompletenessLabel: faltam.length ? `Perfil com ${total - faltam.length} de ${total} informações` : null,
    missingProfileFields: faltam.map(({ key, label }) => ({ key, label })),
    workplaces: x.lugares.map((l) => {
      const endereco = montarEndereco(l.place);
      return {
        placeId: l.place.id,
        placeName: l.place.name,
        addressLabel: endereco.isMissing ? null : [endereco.line1, texto(l.place.addressNeighborhood) || endereco.shortLabel].filter(Boolean).join(' · '),
        mapsUrl: endereco.mapsUrl,
        readiness: l.readiness,
        services: l.servicos
          .map((s) => montarServicoDoLugar(prof, l, s, ctx, now))
          .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
        schedules: [...l.agendas]
          .sort((a, b) => Number(b.card.operation.isOperating) - Number(a.card.operation.isOperating) || String(a.raw.name).localeCompare(String(b.raw.name), 'pt-BR'))
          .map((g) => {
            const { professionalName: _quem, ...agenda } = montarAgendaDoLocal(g, ctx, now);
            return agenda;
          }),
      };
    }),
    activityStats: stats,
    activityEmptyLabel: stats.length ? null : 'Assim que o primeiro cliente marcar, a atividade aparece aqui.',
    pages: x.paginas.map((p) => ({
      id: p.pageId,
      title: p.title,
      typeLabel: TIPO_DE_PAGINA[p.pageType] || 'Página',
      scopeLabel: p.scope === 'place'
        ? `Via ${ctx.locais.get(p.placeId)?.name || 'um local'}`
        : ESCOPO_DA_PAGINA[p.scope] || 'De toda a conta',
      url: `${PAGES_URL}/${p.slug}`,
    })),
    sharedWith: x.compartilhado.map((c) => {
      const niveis = mapaDePermissoes(c.permissions);
      return {
        name: c.grantedToName,
        email: c.grantedToEmail,
        permissionLevels: niveis,
        accessLabel: oQueAOutraContaFaz(niveis),
        sinceLabel: c.createdAt ? `Desde ${dataLonga(c.createdAt)}` : '',
      };
    }),
    absences: x.ausencias.map((a) => ({
      id: a.id,
      label: a.isNow
        ? `Até ${diaCurto(a.endDate, now)}`
        : `${diaCurto(a.startDate, now)} a ${diaCurto(a.endDate, now)} · ${plural(diasEntre(a.startDate, a.endDate), 'dia', 'dias')}`,
      reason: a.reason,
      placesLabel: a.todosOsLocais ? 'Todos os locais' : lista(a.nomes),
      isNow: a.isNow,
    })),
  };
}

// ── resumo, filtros, vazio ───────────────────────────────────────────────────

// Não recebe e não é calmo (pausa do dono, agenda que só a outra conta vê) = precisa de atenção.
const precisaAtencao = (x) => !x.card.readiness.isBookable && x.card.readiness.status !== 'neutral';

function montarResumo(montados, now) {
  const total = montados.length;
  const recebidos = montados.filter((x) => x.prof.isShared).length;
  const recebem = montados.filter((x) => x.card.readiness.isBookable).length;
  const atencao = montados.filter(precisaAtencao).length;
  const pausados = montados.filter((x) => !x.card.readiness.isBookable && x.card.readiness.isByChoice && !agendasOcultas(x.prof)).length;
  const futuros = montados.reduce((s, x) => s + x.mov.futuros, 0);
  const proximo = montados.filter((x) => x.mov.proximo).sort((p, q) => Date.parse(p.mov.proximo) - Date.parse(q.mov.proximo))[0];
  const deOutras = (n) => (n === 1 ? '1 de outra conta' : `${n} de outras contas`);
  const ausentesHoje = montados.filter((x) => x.card.absence?.isNow);
  const ausenteDepois = montados.filter((x) => x.card.absence && !x.card.absence.isNow)
    .sort((p, q) => Date.parse(p.ausencias[0].startDate) - Date.parse(q.ausencias[0].startDate))[0];
  const abertos = montados.filter((x) => (x.card.sharing?.count || 0) > 0).length;

  let absentLabel = null;
  if (ausentesHoje.length === 1) {
    const a = ausentesHoje[0];
    absentLabel = `1 ausente hoje · ${a.prof.name} até ${diaCurto(a.ausencias.find((b) => b.isNow).endDate, now)}`;
  } else if (ausentesHoje.length > 1) absentLabel = `${ausentesHoje.length} ausentes hoje`;
  else if (ausenteDepois) {
    const b = ausenteDepois.ausencias[0];
    absentLabel = `${ausenteDepois.prof.name} fica fora de ${diaCurto(b.startDate, now)} a ${diaCurto(b.endDate, now)}`;
  }

  return {
    total,
    totalLabel: total === 0 ? 'Ninguém na equipe ainda'
      : recebidos === total ? deOutras(total)
        : recebidos > 0 ? `${total} na equipe · ${deOutras(recebidos)}` : `${total} na equipe`,
    bookableCount: recebem,
    bookableLabel: total === 0 ? 'Ninguém recebe agendamento ainda'
      : total === 1 ? (recebem ? 'Recebe agendamento' : 'Não aparece para o cliente agendar')
        : `${recebem} ${recebem === 1 ? 'recebe' : 'recebem'} agendamento`,
    attentionCount: atencao,
    attentionLabel: atencao > 0 ? `${atencao} não ${atencao === 1 ? 'aparece' : 'aparecem'} para o cliente agendar` : null,
    byChoiceLabel: pausados > 0 ? `${pausados} com a agenda pausada por você` : null,
    futureAppointmentsLabel: futuros > 0 ? plural(futuros, 'agendamento marcado', 'agendamentos marcados') : null,
    nextAppointmentLabel: proximo ? `Próximo: ${quando(proximo.mov.proximo, now)} · ${proximo.prof.name}` : null,
    absentLabel,
    sharedOutLabel: abertos > 0 ? `Outras contas acessam ${abertos} da equipe` : null,
    status: total === 0 ? 'neutral'
      : atencao > 0 ? 'error'
        : montados.some((x) => x.card.readiness.status === 'warning') ? 'warning' : 'success',
  };
}

function casaBusca(prof, busca) {
  if (!busca) return true;
  const alvo = normalizar(busca);
  const digitos = busca.replace(/\D/g, '');
  return [prof.name, prof.specialty, prof.email, prof.registrationNumber].some((v) => normalizar(v).includes(alvo))
    || (digitos.length >= 3 && String(prof.phone || '').replace(/\D/g, '').includes(digitos));
}

const ORDENS = {
  name_asc: porNomeSemTitulo,
  name_desc: (p, q) => porNomeSemTitulo(q, p),
  specialty_asc: (p, q) => String(texto(p.prof.specialty) || '￿').localeCompare(String(texto(q.prof.specialty) || '￿'), 'pt-BR') || porNomeSemTitulo(p, q),
  next_appointment: (p, q) => (p.mov.proximo ? Date.parse(p.mov.proximo) : Infinity) - (q.mov.proximo ? Date.parse(q.mov.proximo) : Infinity) || porNomeSemTitulo(p, q),
  busiest: (p, q) => q.peso - p.peso || porNomeSemTitulo(p, q),
};

// ── entrada ──────────────────────────────────────────────────────────────────

/**
 * @param {Object} args
 * @param {Object} args.data        useProfessionals().items — GET /api/professionals: { professionals, places,
 *                                  services, payments, activity, pages }
 * @param {Array}  args.schedules   GET /api/schedules (com as agendas compartilhadas e as métricas)
 * @param {Object|null} args.entities GET /api/entities — turnos, serviços de cada turno, ausências e vínculos
 *                                  da conta; null = ainda não chegou (a tela segura em 'loading')
 * @param {Object} [args.turnosExtras] { [scheduleId]: ScheduleShiftDto[] } — turnos das agendas de recebidos
 *                                  (GET /api/schedules/{id}/shifts), que o espelho não traz
 * @param {Object|null} args.access GET /api/access — { items } (quem tem acesso a cada pessoa da equipe)
 * @param {Object} [args.horarios]  { [chaveDosHorarios]: { state, slots } } — "próximos horários" pedidos
 * @param {Function} args.getLabel tema » labels
 * @param {Object} args.filters     { searchText, sortOrder, segment }
 * @param {string|null} args.selectedId
 * @param {boolean} args.loading
 * @param {*} args.error
 * @param {(donoId:string, profissionalId:string)=>string} args.fotoDe
 * @param {Date} [args.now]
 */
export function buildProfessionalsVm({
  data = {},
  schedules = [],
  entities = null,
  turnosExtras = {},
  access = null,
  horarios = {},
  getLabel,
  filters = {},
  selectedId = null,
  loading = false,
  error = null,
  fotoDe = () => '',
  now = new Date(),
}) {
  const { labels, examples } = buildProfessionalsLabels(getLabel);
  const { searchText = '', sortOrder = 'name_asc', segment = 'all' } = filters;
  const profissionais = data.professionals || [];

  const filtrosBase = (segmentOptions, isFiltered, resultLabel) => ({
    searchText, sortOrder, sortOptions: SORT_OPTIONS, segment, segmentOptions,
    isFiltered, canClear: isFiltered, resultLabel,
  });
  const vazio = (viewState, errorMessage) => ({
    viewState, errorMessage, labels, permissionAreas: AREAS_DO_COMPARTILHAMENTO, summary: montarResumo([], now), planUsage: null,
    filters: filtrosBase([], false, null), groups: [], emptyState: null, selectedId: null, detail: null,
  });

  // Erro tem precedência: falhou, mostra a falha (nunca um skeleton eterno).
  if (error) return vazio('error', 'Não foi possível carregar agora. Verifique sua conexão e tente de novo.');

  // Turnos e ausências vêm de /entities, separados da lista. Sem eles toda pessoa pareceria não
  // receber agendamento — acusar sem ter os dados é pior do que esperar (mesma guarda de Locais).
  if (loading || (profissionais.length > 0 && !entities)) return vazio('loading', null);

  const ctx = montarContexto(data, schedules, entities, turnosExtras, access, horarios, fotoDe);
  const montados = profissionais.map((prof) => montarCard(prof, ctx, now));
  const summary = montarResumo(montados, now);

  if (montados.length === 0) {
    return {
      ...vazio('empty', null),
      summary,
      emptyState: {
        situation: 'firstTime',
        title: 'Monte sua equipe',
        body: 'Cada pessoa da equipe aparece na sua página para o cliente escolher e marcar horário.',
        actionLabel: labels.actionLabel,
        examples,
      },
    };
  }

  const SEGMENTOS = {
    all: () => true,
    attention: precisaAtencao,
    shared: (x) => (x.card.sharing?.count || 0) > 0,
    received: (x) => !!x.prof.isShared,
    absent: (x) => !!x.card.absence,
  };
  const segmentoValido = SEGMENTOS[segment] ? segment : 'all';
  const segmentOptions = [
    { value: 'all', label: 'Todos', count: montados.length },
    { value: 'attention', label: 'Não aparecem para agendar', count: montados.filter(SEGMENTOS.attention).length },
    { value: 'shared', label: 'Abertos para outras contas', count: montados.filter(SEGMENTOS.shared).length },
    { value: 'received', label: 'De outras contas', count: montados.filter(SEGMENTOS.received).length },
    { value: 'absent', label: 'Com ausência marcada', count: montados.filter(SEGMENTOS.absent).length },
  ].filter((o) => o.value === 'all' || o.count > 0);

  const visiveis = montados
    .filter((x) => SEGMENTOS[segmentoValido](x) && casaBusca(x.prof, searchText.trim()))
    .sort(ORDENS[sortOrder] || ORDENS.name_asc);
  const isFiltered = !!searchText.trim() || segmentoValido !== 'all';
  const proprios = visiveis.filter((x) => !x.prof.isShared);
  const recebidos = visiveis.filter((x) => x.prof.isShared);
  const temProprio = montados.some((x) => !x.prof.isShared);

  const groups = [];
  if (proprios.length > 0 || (!isFiltered && recebidos.length > 0)) {
    groups.push({
      key: 'own', title: labels.ownGroupTitle, description: null, items: proprios.map((x) => x.card),
      emptyLabel: !temProprio ? 'Você ainda não cadastrou profissionais próprios — os de baixo são de outras contas.' : null,
    });
  }
  if (recebidos.length > 0) {
    groups.push({ key: 'received', title: labels.receivedGroupTitle, description: labels.receivedGroupDescription, items: recebidos.map((x) => x.card), emptyLabel: null });
  }

  // Uma pessoa só e sem filtro: abre direto — é o caso de 858 de 1.006 contas.
  const unico = montados.length === 1 && !isFiltered ? montados[0].card.id : null;
  const aberto = montados.find((x) => x.card.id === (selectedId || unico)) || null;

  return {
    viewState: 'ready',
    errorMessage: null,
    labels,
    permissionAreas: AREAS_DO_COMPARTILHAMENTO,
    summary,
    planUsage: null, // Plans:EnforcementEnabled=false em PRD — ver o contrato.
    filters: filtrosBase(segmentOptions, isFiltered, isFiltered ? `${visiveis.length} de ${montados.length}` : null),
    groups,
    emptyState: visiveis.length === 0
      ? {
        situation: 'filtered',
        title: searchText.trim() ? `Ninguém encontrado para "${searchText.trim()}"` : 'Ninguém neste filtro',
        body: 'Confira a grafia ou busque pela especialidade, pelo telefone ou pelo registro.',
        actionLabel: searchText.trim() ? 'Limpar busca' : 'Ver todos',
        examples: [],
      }
      : null,
    selectedId: aberto ? aberto.card.id : null,
    detail: aberto ? montarDetalhe(aberto, ctx, now) : null,
  };
}

/** Índices montados uma vez por render: tudo vira Map por id. */
function montarContexto(data, schedules, entities, turnosExtras, access, horarios, fotoDe) {
  const servicos = new Map([...(entities?.services || []), ...(data.services || [])].map((s) => [s.id, s]));
  const turnos = { ...montarTurnos(entities, (id) => servicos.get(id)?.name || null), ...(turnosExtras || {}) };

  const agendas = [];
  for (const s of schedules || []) {
    if (String(s.status || 'Active').toLowerCase() === 'deleted') continue;
    agendas.push({ raw: s, card: buildScheduleCard(s, turnos[s.id] || []) });
  }

  const agrupar = (itens, chave) => {
    const m = new Map();
    for (const item of itens || []) {
      if (!m.has(item[chave])) m.set(item[chave], []);
      m.get(item[chave]).push(item);
    }
    return m;
  };

  const itensDeAcesso = access?.items || [];
  const recebidoDesde = new Map();
  for (const i of itensDeAcesso.filter((x) => x.direction !== 'given')) {
    const atual = recebidoDesde.get(i.professionalId);
    if (!atual || Date.parse(i.createdAt) < Date.parse(atual)) recebidoDesde.set(i.professionalId, i.createdAt);
  }

  return {
    servicos,
    turnos,
    agendasDe: (profId, placeId) => agendas.filter((g) => g.raw.professionalId === profId && g.raw.placeId === placeId),
    agendasDaPessoa: (profId) => agendas.filter((g) => g.raw.professionalId === profId),
    locais: new Map((data.places || []).map((p) => [p.id, p])),
    pagamentos: new Map((data.payments || []).map((p) => [p.id, p])),
    profissionais: new Map((data.professionals || []).map((p) => [p.id, p])),
    // Vínculo pessoa × local → local: vem na própria resposta (vale também para os recebidos).
    localDoVinculo: new Map((data.professionals || []).flatMap((p) => (p.places || []).map((l) => [l.id, l.placeId]))),
    bloqueios: entities?.scheduleBlocks || [],
    atividade: new Map((data.activity || []).map((a) => [a.professionalId, a])),
    paginas: agrupar(data.pages, 'professionalId'),
    compartilhadoCom: agrupar(itensDeAcesso.filter((x) => x.direction === 'given'), 'professionalId'),
    recebidoDesde,
    horarios: horarios || {},
    fotoDe,
  };
}
